
import React, { useState } from "react";
import type { LegalModuleConfig } from "./types";
import { useLegalCrudModule } from "./useLegalCrudModule";
import { DataToolbar, ActionButtons } from "../../components/DataToolbar";
import { Modal } from "../../components/Modal";
import { PreviewModal } from "../../components/PreviewModal";
import { ContractForm } from "./forms/ContractForm";
import { useAiContext } from "../../ai/AiContext"; // Import Context

// Registry for Forms
const FORM_REGISTRY: Record<string, React.ComponentType<any>> = {
  "ContractForm": ContractForm,
  // Add other forms here later
};

interface Props {
  config: LegalModuleConfig;
}

export function LegalListModule({ config }: Props) {
  const {
    items,
    filteredItems,
    loading,
    error,
    searchText,
    setSearchText,
    showLegacy,
    setShowLegacy,
    handleDelete,
    handleImport,
    handleExport
  } = useLegalCrudModule(config);

  const { setActiveItem } = useAiContext(); // Use Context

  // Modal States
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [previewItem, setPreviewItem] = useState<any | null>(null);
  
  // Import Modal State
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);

  const FormComponent = FORM_REGISTRY[config.formSchema];

  const onImportSubmit = async () => {
    if (!importFile) return;
    setIsImporting(true);
    await handleImport(importFile);
    setIsImporting(false);
    setIsImportModalOpen(false);
    setImportFile(null);
  };

  const handleSetPreview = (item: any) => {
      setPreviewItem(item);
      // Set AI Context for Generic Lists
      setActiveItem({
          type: (config.id === 'contracts' ? 'contract' : 'other'),
          id: item.contractNo || item.id,
          title: item.title,
          data: item,
          attachments: item.attachments
      });
  };

  const handleSetEdit = (item: any) => {
      setEditingItem(item);
      setIsFormOpen(true);
      // Also set AI context for edit mode to allow helper access
      setActiveItem({
          type: (config.id === 'contracts' ? 'contract' : 'other'),
          id: item.contractNo || item.id,
          title: item.title,
          data: item,
          attachments: item.attachments
      });
  };

  const handleClosePreview = () => {
      setPreviewItem(null);
      setActiveItem(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 500 }}>
      {/* Search & Toolbar Area */}
      <div className="section" style={{ marginBottom: 16, padding: "12px 16px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
          <input 
            className="input" 
            style={{ width: 300 }} 
            placeholder={`搜尋${config.searchableFields.join(", ")}...`} 
            value={searchText} 
            onChange={e => setSearchText(e.target.value)} 
          />
          {config.id === 'contracts' && (
              <label style={{display:'flex', alignItems:'center', gap:6, fontSize:13, cursor:'pointer', userSelect:'none'}}>
                  <input 
                    type="checkbox" 
                    checked={showLegacy} 
                    onChange={e => setShowLegacy(e.target.checked)} 
                  />
                  顯示舊合約
              </label>
          )}
        </div>
        
        <DataToolbar 
          title={config.title}
          canEdit={!!config.canEdit}
          onCreate={() => { setEditingItem(null); setIsFormOpen(true); setActiveItem(null); }}
          onImport={config.importMapping ? () => setIsImportModalOpen(true) : undefined}
          onExport={handleExport}
        />
      </div>

      {error && <div className="err" style={{marginBottom:16}}>讀取失敗: {error.message}</div>}

      {/* Table Area - Removed 'card' class to fix width constriction, added explicit width 100% */}
      <div style={{ 
          padding: 0, 
          flex: 1, 
          overflow: "hidden", 
          display: "flex", 
          flexDirection: "column", 
          background: "#161616", 
          border: "1px solid var(--border)",
          borderRadius: 16, // Match standard radius
          width: "100%",
          maxWidth: "100%"
      }}>
        <div style={{ overflow: "auto", flex: 1 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead style={{ background: "#222", color: "#aaa", textAlign: "left", position: "sticky", top: 0, zIndex: 1 }}>
              <tr>
                {config.columns.map(col => (
                  <th key={col.key} style={{ padding: "12px 16px", width: col.width, whiteSpace: "nowrap" }}>{col.label}</th>
                ))}
                <th style={{ padding: "12px 16px", textAlign: "right", whiteSpace: "nowrap" }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={config.columns.length + 1} style={{padding:20, textAlign:'center'}}>載入中...</td></tr>}
              {!loading && filteredItems.map((row) => (
                <tr key={row.id} style={{ borderBottom: "1px solid #333" }}>
                  {config.columns.map(col => (
                    <td key={col.key} style={{ padding: "12px 16px", verticalAlign: "top" }}>
                      {col.key === 'contractNo' && row.isLegacy && (
                          <span className="badge" style={{marginRight:6, fontSize:10, padding:'2px 4px', background:'#666', color:'#fff'}}>舊</span>
                      )}
                      {col.render ? col.render(row) : (
                        col.type === 'badge' ? <span className="badge">{row[col.key]}</span> :
                        col.type === 'date' ? <span style={{color:'var(--muted)'}}>{row[col.key]}</span> :
                        row[col.key]
                      )}
                    </td>
                  ))}
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <ActionButtons 
                      canEdit={!!config.canEdit}
                      onPreview={() => handleSetPreview(row)}
                      onEdit={() => handleSetEdit(row)}
                      onDelete={() => handleDelete(row.id, row)}
                    />
                  </td>
                </tr>
              ))}
              {!loading && filteredItems.length === 0 && <tr><td colSpan={config.columns.length + 1} style={{padding:40, textAlign:'center', color: 'var(--muted)'}}>無資料</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {isFormOpen && FormComponent && (
        <FormComponent 
          initialData={editingItem}
          allRows={items} 
          onClose={() => { setIsFormOpen(false); setActiveItem(null); }}
          onSuccess={() => { setIsFormOpen(false); setEditingItem(null); setActiveItem(null); }}
        />
      )}

      {/* Preview Modal */}
      {previewItem && <PreviewModal data={previewItem} onClose={handleClosePreview} />}

      {/* Import Modal */}
      {isImportModalOpen && (
        <Modal title="匯入資料" onClose={() => setIsImportModalOpen(false)} width={500}>
           <div className="p" style={{marginBottom:10}}>請選擇 CSV 檔案進行匯入</div>
           <div style={{border:'2px dashed #444', padding:20, textAlign:'center'}}>
              <input type="file" accept=".csv" onChange={e => setImportFile(e.target.files?.[0] || null)} />
           </div>
           <div className="row" style={{marginTop:20, justifyContent:'flex-end'}}>
              <button className="btn" onClick={onImportSubmit} disabled={!importFile || isImporting}>
                 {isImporting ? "匯入中..." : "開始匯入"}
              </button>
           </div>
        </Modal>
      )}
    </div>
  );
}
